import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import { ClosePayPeriodRequest, ProcessingRunRequest, useApi } from '../index';
import { ToolsCacheKeys } from './util/cache-keys';

export const useProcessingSetupGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [ToolsCacheKeys.ProcessingSetup],
    queryFn: () =>
      api.toolsV2Api
        .apiV2ToolsProcessingSetupGet()
        .then((response) => response?.data),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useProcessingPost = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const queryClient = useQueryClient();
  return useMutation(
    (body: ProcessingRunRequest) =>
      api.toolsV2Api
        .apiV2ToolsProcessingPost({ processingRunRequest: body })
        .then((r) => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ToolsCacheKeys.ProcessingSetup]);
      },
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useProcessingJobStatusGet = () => {
  const api = useApi();
  const { data: setup } = useProcessingSetupGet();
  const jobId = setup?.activeJobId ?? null;
  return useQuery({
    queryKey: [ToolsCacheKeys.ProcessingJobStatus, jobId],
    queryFn: () =>
      api.toolsV2Api
        .apiV2ToolsProcessingJobIdGet({ jobId: jobId! })
        .then((r) => r.data),
    enabled: !!jobId,
    refetchInterval: (data) => (data?.isActive ? 2000 : false),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useProcessingJobDelete = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation(
    (jobId: string) =>
      api.toolsV2Api
        .apiV2ToolsProcessingJobIdDelete({ jobId })
        .then((r) => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ToolsCacheKeys.ProcessingJobStatus]);
        queryClient.invalidateQueries([ToolsCacheKeys.ProcessingSetup]);
      },
    }
  );
};

export const useClosePayPeriodSetupGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [ToolsCacheKeys.ClosePayPeriodSetup],
    queryFn: () =>
      api.closePayPeriodV2Api
        .apiV2ToolsClosePayPeriodSetupGet()
        .then((r) => r.data),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useClosePayPeriodPost = () => {
  const api = useApi();
  const { notifyError, notifyWarning, notifySuccess } = useSnackBar();
  return useMutation(
    (body: ClosePayPeriodRequest) =>
      api.closePayPeriodV2Api
        .apiV2ToolsClosePayPeriodPost({ closePayPeriodRequest: body })
        .then((r) => r.data),
    {
      onSuccess: (data) => {
        if (!data.closedPayPeriods?.length) {
          notifyWarning('Nothing to close');
        } else {
          notifySuccess('Pay period closed successfully');
        }
      },
      onError: async (error) => {
        notifyError(getApiErrorMessage(error));
      },
    }
  );
};

export const useTimeClockGroupsGet = (enabled: boolean) => {
  const api = useApi();
  return useQuery({
    queryKey: [ToolsCacheKeys.ClockGroups],
    queryFn: () =>
      api.timeClockApi.apiV2TimeclockGroupsGet().then((r) => r.data ?? []),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
